import type { CanvasView } from "./viewport";

export type CameraScheduler = {
  frame: (callback: () => void) => number;
  cancelFrame: (id: number) => void;
  delay: (callback: () => void) => number;
  cancelDelay: (id: number) => void;
};

const browserScheduler: CameraScheduler = {
  frame: (callback) => requestAnimationFrame(callback),
  cancelFrame: (id) => cancelAnimationFrame(id),
  delay: (callback) => window.setTimeout(callback, 180),
  cancelDelay: (id) => window.clearTimeout(id),
};

function sameView(a: CanvasView, b: CanvasView) {
  return a.zoom === b.zoom && a.pan.x === b.pan.x && a.pan.y === b.pan.y;
}

/** Input stays current immediately; only the small camera subscribers paint per frame. */
export class CanvasCamera {
  readonly viewRef: { current: CanvasView };
  private snapshot: { view: CanvasView; moving: boolean };
  private listeners = new Set<() => void>();
  private frame: number | null = null;
  private delay: number | null = null;
  private dirty = false;

  constructor(
    view: CanvasView,
    public onCommit?: (view: CanvasView) => void,
    private scheduler = browserScheduler,
  ) {
    this.viewRef = { current: view };
    this.snapshot = { view, moving: false };
  }

  getView = () => this.viewRef.current;
  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  private publish(moving: boolean) {
    if (sameView(this.snapshot.view, this.getView()) && this.snapshot.moving === moving) return;
    this.snapshot = { view: this.getView(), moving };
    this.listeners.forEach((listener) => listener());
  }

  setView = (view: CanvasView) => {
    if (sameView(this.getView(), view)) return;
    this.viewRef.current = view;
    this.dirty = true;
    if (this.frame === null) this.frame = this.scheduler.frame(() => {
      this.frame = null;
      this.publish(true);
    });
    if (this.delay !== null) this.scheduler.cancelDelay(this.delay);
    this.delay = this.scheduler.delay(this.flush);
  };

  private cancel() {
    if (this.frame !== null) this.scheduler.cancelFrame(this.frame);
    if (this.delay !== null) this.scheduler.cancelDelay(this.delay);
    this.frame = this.delay = null;
  }

  /** Also called before pagehide/unmount, when an animation frame may never run. */
  flush = () => {
    this.cancel();
    const dirty = this.dirty;
    this.dirty = false;
    this.publish(false);
    if (dirty) this.onCommit?.(this.getView());
  };

  /** An explicit restored/page/focus view supersedes any queued input. */
  restore(view: CanvasView) {
    if (sameView(this.getView(), view)) return;
    this.cancel();
    this.dirty = false;
    this.viewRef.current = view;
    this.publish(false);
  }
}
